'use client';

import {
  Permission,
  FeedbackInviteStatus,
  type FeedbackInvite,
} from '@up2stay/up2promo-api-client';
import { Button, Skeleton } from '@up2stay/up2promo-ui';
import { Mail, Plus } from 'lucide-react';
import { useMemo, useState } from 'react';

import { FeedbackInviteListRow } from '@/core/components/feedback/feedback-invite-list-row';
import { FEEDBACK_LIST_SURFACE_CLASS } from '@/core/components/feedback/feedback-surface';
import {
  ListEmptyState,
  ListFilterSelect,
  ListPageBody,
  ListSearchField,
  ListToolbarSurface,
} from '@/core/components/layout';
import { useFeedbackInvites } from '@/core/hooks/use-feedback';
import { useLocations } from '@/core/hooks/use-locations';
import { useLocale } from '@/core/hooks/use-locale';
import { useNavigateShell } from '@/core/hooks/use-navigate-shell';
import { usePermissions } from '@/core/hooks/use-permissions';
import { useDictionary } from '@/core/providers/dictionary-provider';
import { shellPaths } from '@/core/router/app-paths';
import { useCurrentWorkspace } from '@/core/store/workspace.store';

const ALL = '__all__';

function inviteMatchesSearch(
  invite: FeedbackInvite,
  query: string,
  locationLabel: string,
): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const haystack = [invite.title, invite.contextLabel ?? '', locationLabel]
    .join(' ')
    .toLowerCase();
  return haystack.includes(q);
}

export function FeedbackInvitesPanel() {
  const dict = useDictionary().feedback;
  const locale = useLocale();
  const { navigateShell } = useNavigateShell();
  const { can } = usePermissions();
  const canInvite = can(Permission.FEEDBACK_INVITE);
  const workspace = useCurrentWorkspace();
  const workspaceId = workspace?.id;
  const { locations } = useLocations();
  const [searchQuery, setSearchQuery] = useState('');
  const [status, setStatus] = useState<FeedbackInviteStatus | typeof ALL>(ALL);
  const [locationId, setLocationId] = useState<string>(ALL);

  const {
    data: invites = [],
    isLoading,
    error,
    refetch,
  } = useFeedbackInvites(workspaceId, {
    status: status === ALL ? undefined : status,
    limit: 50,
  });

  const dateFmt = useMemo(
    () =>
      new Intl.DateTimeFormat(locale === 'de' ? 'de-DE' : 'en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      }),
    [locale],
  );

  const locationName = (id: string) =>
    locations.find((l) => l.id === id)?.name ?? id;

  const filteredInvites = useMemo(() => {
    return invites.filter((invite) => {
      if (locationId !== ALL && invite.locationId !== locationId) {
        return false;
      }
      const loc =
        locations.find((l) => l.id === invite.locationId)?.name ??
        invite.locationId;
      return inviteMatchesSearch(invite, searchQuery, loc);
    });
  }, [invites, locationId, locations, searchQuery]);

  const inviteDateLabel = (invite: FeedbackInvite) => {
    const raw = invite.sendAt ?? invite.firstSentAt ?? invite.createdAt;
    return dateFmt.format(new Date(raw));
  };

  return (
    <ListPageBody>
      <ListToolbarSurface>
        <div className="flex min-w-0 flex-col gap-2 sm:flex-row sm:items-center">
          <ListSearchField
            className="min-w-0 w-full sm:flex-1"
            variant="toolbar"
            type="search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={dict.invites.searchPlaceholder}
            aria-label={dict.invites.searchPlaceholder}
            onClear={() => setSearchQuery('')}
          />
          <div className="flex min-w-0 w-full flex-wrap items-center gap-2 sm:w-auto">
            <ListFilterSelect
              ariaLabel={dict.invites.filters.status}
              value={status}
              onValueChange={(v) =>
                setStatus(v as FeedbackInviteStatus | typeof ALL)
              }
              options={[
                { value: ALL, label: dict.invites.filters.allStatuses },
                ...(
                  [
                    FeedbackInviteStatus.DRAFT,
                    FeedbackInviteStatus.SCHEDULED,
                    FeedbackInviteStatus.SENDING,
                    FeedbackInviteStatus.ACTIVE,
                    FeedbackInviteStatus.COMPLETED,
                    FeedbackInviteStatus.CANCELLED,
                  ] as const
                ).map((s) => ({
                  value: s,
                  label: dict.inviteStatus[s as keyof typeof dict.inviteStatus],
                })),
              ]}
            />
            <ListFilterSelect
              ariaLabel={dict.invites.filters.location}
              value={locationId}
              onValueChange={setLocationId}
              options={[
                { value: ALL, label: dict.invites.filters.allLocations },
                ...locations.map((loc) => ({
                  value: loc.id,
                  label: loc.name ?? loc.id,
                })),
              ]}
            />
          </div>
        </div>
      </ListToolbarSurface>

      {isLoading && (
        <div className="flex flex-col gap-2">
          <Skeleton className="h-16 w-full rounded-[var(--r-card)]" />
          <Skeleton className="h-16 w-full rounded-[var(--r-card)]" />
        </div>
      )}

      {error && (
        <ListEmptyState
          title={dict.invites.loadFailed}
          action={
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => void refetch()}
            >
              {dict.retry}
            </Button>
          }
        />
      )}

      {!isLoading && !error && invites.length === 0 && (
        <ListEmptyState
          icon={<Mail className="h-6 w-6 text-foreground/70" />}
          title={dict.invites.empty.title}
          description={dict.invites.empty.description}
          action={
            canInvite ? (
              <Button
                type="button"
                size="sm"
                onClick={() => navigateShell(shellPaths.feedbackCompose)}
              >
                <Plus className="h-4 w-4" />
                {dict.invites.create}
              </Button>
            ) : undefined
          }
        />
      )}

      {!isLoading &&
        !error &&
        invites.length > 0 &&
        filteredInvites.length === 0 && (
          <ListEmptyState
            icon={<Mail className="h-6 w-6 text-foreground/70" />}
            title={dict.invites.empty.noResultsTitle}
            description={dict.invites.empty.noResultsDescription}
          />
        )}

      {!isLoading && !error && filteredInvites.length > 0 ? (
        <ul className={FEEDBACK_LIST_SURFACE_CLASS}>
          {filteredInvites.map((invite) => (
            <FeedbackInviteListRow
              key={invite.id}
              invite={invite}
              locationName={locationName(invite.locationId)}
              dateLabel={inviteDateLabel(invite)}
              copy={dict.invites.row}
              onOpen={() =>
                navigateShell(shellPaths.feedbackInviteDetail(invite.id))
              }
            />
          ))}
        </ul>
      ) : null}
    </ListPageBody>
  );
}
